import { type Reducer, useEffect, useReducer } from 'react';
import type { Source } from '@epimodev/callbag-doki';
import subscribe from '@epimodev/callbag-doki/utils/subscribe';

import { assertNever } from 'app/utils/types';
import { ERROR_NOT_FOUND, ERROR_UNKNOWN } from 'app/constants/errors';

interface SubscriptionOptions {
  prevent: boolean;
}

interface SubscriptionState<T> {
  data: T | undefined;
  errorType: ErrorType | undefined;
  status: RequestStatus;
}

interface CancelSubscription {
  type: 'CANCEL_SUBSCRIPTION';
}
interface StartSubscription {
  type: 'START_SUBSCRIPTION';
}
interface SuscriptionSuccess<T> {
  type: 'SUBSCRIPTION_RECEIVE';
  payload: {
    value: T;
    mergeData: (currentData: T | undefined, newData: T) => T;
  };
}
interface SuscriptionComplete {
  type: 'SUBSCRIPTION_COMPLETE';
}
interface SuscriptionFail {
  type: 'SUBSCRIPTION_FAIL';
  payload: ErrorType;
}
type SubscriptionAction<T> =
  | CancelSubscription
  | StartSubscription
  | SuscriptionSuccess<T>
  | SuscriptionComplete
  | SuscriptionFail;

const createInitialState = <T>(): SubscriptionState<T> => ({
  data: undefined,
  status: 'NOT_STARTED',
  errorType: undefined
});

const reducer = <T>(
  state: SubscriptionState<T>,
  action: SubscriptionAction<T>
): SubscriptionState<T> => {
  switch (action.type) {
    case 'CANCEL_SUBSCRIPTION':
      return {
        ...state,
        status: 'NOT_STARTED',
        errorType: undefined
      };
    case 'START_SUBSCRIPTION':
      return {
        ...state,
        status: 'LOADING',
        errorType: undefined
      };
    case 'SUBSCRIPTION_RECEIVE':
      return {
        ...state,
        data: action.payload.mergeData(state.data, action.payload.value),
        status: 'RECEIVING',
        errorType: undefined
      };
    case 'SUBSCRIPTION_COMPLETE':
      return {
        ...state,
        status: 'COMPLETED',
        errorType: undefined
      };
    case 'SUBSCRIPTION_FAIL':
      return {
        ...state,
        status: 'FAILED',
        errorType: action.payload
      };
    default:
      throw assertNever(action);
  }
};

const useSubscription = <T>(
  createSource: (currentData: T | undefined) => Source<T | Error>,
  deps: any[],
  { prevent = false }: Partial<SubscriptionOptions> = {},
  mergeData: (currentData: T | undefined, newData: T) => T = (_currentData, newData) => newData
): SubscriptionState<T> => {
  const initialState = createInitialState<T>();
  const [state, dispatch] = useReducer<Reducer<SubscriptionState<T>, SubscriptionAction<T>>>(
    reducer,
    initialState
  );

  useEffect(() => {
    if (prevent) {
      dispatch({ type: 'CANCEL_SUBSCRIPTION' });
    } else {
      dispatch({ type: 'START_SUBSCRIPTION' });

      const source = createSource(state.data);
      const unsubscribe = subscribe(source)({
        next: value => {
          if (value instanceof Error) {
            const errorType = value.message === ERROR_NOT_FOUND ? ERROR_NOT_FOUND : ERROR_UNKNOWN;
            return dispatch({ type: 'SUBSCRIPTION_FAIL', payload: errorType });
          }
          return dispatch({ type: 'SUBSCRIPTION_RECEIVE', payload: { value, mergeData } });
        },
        error: e => {
          const errorType =
            e instanceof Error && e.message === ERROR_NOT_FOUND ? ERROR_NOT_FOUND : ERROR_UNKNOWN;
          return dispatch({ type: 'SUBSCRIPTION_FAIL', payload: errorType });
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [...deps, prevent]);

  return state;
};

export { useSubscription };
